/**
 * Step 01 — the name, and the language the captions come out in (spec §2.2).
 *
 * Two answers on one screen because they are one decision in the operator's
 * head ("this is my brand, in this language"), and because the name is what
 * every later screen shows back to them. The name also seeds the slug and the
 * wordmark, so it is the first thing asked and the first thing previewed.
 */
import { useEffect, useState, type Dispatch, type RefObject } from "react";
import { Globe } from "lucide-react";
import { LANGUAGE_PRESETS, directionForCode, makeCustomLanguage } from "../../../lib/languages";
import { Select } from "../../../components/ui/Select";
import type { BrandSetupAction, BrandSetupState } from "../../../lib/brandSetup/state";
import { slugify } from "../../../lib/brandSetup/payload";
import { BIG_INPUT, Block, MONO_LABEL, REASON, SELECT } from "../ui";
import { REASON_ID, useStepReason } from "../StepFrame";

const CUSTOM = "__custom";

export function BasicsStep(props: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
  /** The shell focuses this when Continue has nothing to work with. */
  nameRef: RefObject<HTMLInputElement | null>;
}): React.JSX.Element {
  const { state, dispatch, nameRef } = props;
  const reason = useStepReason();
  const known = LANGUAGE_PRESETS.some((p) => p.code === state.language.code);
  const [customOn, setCustomOn] = useState(!known);
  const [customName, setCustomName] = useState(known ? "" : state.language.displayName);
  const [customCode, setCustomCode] = useState(known ? "" : state.language.code);
  const [customDir, setCustomDir] = useState<"rtl" | "ltr" | null>(known ? null : state.language.direction);
  const effCustomDir: "rtl" | "ltr" = customDir ?? directionForCode(customCode);
  const codeValid = /^[a-z]{2,3}$/.test(customCode.trim());

  // A custom language is only worth writing once it has a code — until then
  // the state keeps the last real preset, so the preview never goes blank.
  useEffect(() => {
    if (!customOn || !codeValid) return;
    const next = makeCustomLanguage(customName, customCode, effCustomDir);
    // Nothing to say: on MOUNT (a restored custom language) this effect would
    // otherwise re-dispatch the language the state already holds, which
    // re-seeds the font roles and claims the field for no reason.
    const current = state.language;
    if (next.code === current.code && next.displayName === current.displayName && next.direction === current.direction)
      return;
    dispatch({ type: "language", language: next });
  }, [customOn, codeValid, customName, customCode, effCustomDir, dispatch, state.language]);

  return (
    <div style={{ display: "grid", gap: 26 }}>
      <Block label="Your brand's name">
        <input
          ref={nameRef}
          data-autofocus
          value={state.brandName}
          onChange={(e) =>
            dispatch({
              type: "patch",
              patch: {
                brandName: e.target.value,
                ...(state.slugTouched ? {} : { slug: slugify(e.target.value) }),
              },
            })
          }
          placeholder={"e.g. Court & Clair"}
          aria-label="Your brand's name"
          aria-describedby={reason ? REASON_ID : undefined}
          aria-invalid={reason ? true : undefined}
          style={BIG_INPUT}
        />
        {reason && (
          <div id={REASON_ID} style={{ ...REASON, marginTop: 10 }}>
            {reason}
          </div>
        )}
      </Block>

      <Block label="Caption language">
        <Select
          value={customOn ? CUSTOM : state.language.code}
          onChange={(v) => {
            if (v === CUSTOM) {
              setCustomOn(true);
              return;
            }
            setCustomOn(false);
            const preset = LANGUAGE_PRESETS.find((p) => p.code === v);
            if (preset) dispatch({ type: "language", language: preset });
          }}
          aria-label="Caption language"
          size="lg"
          enterSubmits
          style={{ maxWidth: 360 }}
          options={[
            ...LANGUAGE_PRESETS.map((p) => ({
              value: p.code,
              // Both names: ours so the list can be scanned, theirs so a
              // speaker recognises their own language at a glance — and the
              // flag, which is found before either is read.
              label: p.displayName === p.nativeName ? p.displayName : `${p.displayName} · ${p.nativeName}`,
              icon: <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>{p.flag}</span>,
            })),
            { value: CUSTOM, label: "Another language…", icon: <Globe size={14} /> },
          ]}
        />

        {customOn && (
          <div
            style={{
              display: "grid",
              gap: 14,
              marginTop: 14,
              padding: 16,
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,.08)",
              background: "rgba(255,255,255,.02)",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 120px", gap: 12 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={MONO_LABEL}>Language</span>
                <input
                  value={customName}
                  placeholder="e.g. Polish"
                  onChange={(e) => setCustomName(e.target.value)}
                  style={SELECT}
                />
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span style={MONO_LABEL}>Code</span>
                <input
                  value={customCode}
                  maxLength={3}
                  placeholder="pl"
                  onChange={(e) => setCustomCode(e.target.value.toLowerCase().replace(/[^a-z]/g, ""))}
                  style={{ ...SELECT, fontFamily: "var(--ql-font-mono)" }}
                />
              </label>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {([["ltr", "Left to right"], ["rtl", "Right to left"]] as const).map(([dir, label]) => {
                const on = effCustomDir === dir;
                return (
                  <button
                    key={dir}
                    type="button"
                    onClick={() => setCustomDir(dir)}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: 10,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: 13,
                      fontWeight: 600,
                      border: on ? "1px solid var(--ql-yellow)" : "1px solid rgba(255,255,255,.10)",
                      background: on ? "rgba(255,213,0,.07)" : "rgba(255,255,255,.02)",
                      color: on ? "var(--ql-fg)" : "var(--ql-fg-muted)",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Block>
    </div>
  );
}
